import { createSlice } from "@reduxjs/toolkit";
import axiosInstanceWebPortal from "../helper/axiosInstanceWebPortal";
import { toast } from "react-toastify";
import { ApiConnection } from "../config/env";

const initialState = {
  userType: "UNREGISTERED", // "businessUser" when logged in
  loginSource: null,        // "businessPortal" only when logged in via /businessSubscriptionPortal/login
  userDetails: null,
  loginLoading: false,
  logoutLoading: false,
  // Cards
  cards: [],
  cardsLoading: false,
  deleteLoading: false,
  setPrimaryLoading: false,
  addCardLoading: false,
  subscribeLoading: false,
  cancelLoading: false,
  gatewayLoading: false,
};

export const businessAuthSlice = createSlice({
  name: "businessAuth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loginLoading = true;
    },
    loginFailed: (state) => {
      state.loginLoading = false;
    },
    loginSuccess: (state, action) => {
      console.log('acrion from',action.payload);
      state.loginLoading = false;
      state.userDetails = action.payload;
      state.userType = "businessUser";
    },
    autoLoginSuccess: (state, action) => {
      state.userDetails = action.payload;
      state.userType = "businessUser";
    },
    // Set loginSource — only called explicitly after a direct business portal login
    setLoginSource: (state, action) => {
      state.loginSource = action.payload;
    },
    // Populate userDetails for UMP subscription thunks WITHOUT granting portal access
    setBusinessUserDetails: (state, action) => {
      state.userDetails = action.payload;
      // userType and loginSource intentionally NOT changed
    },
    logoutBusinessLocal: (state) => {
      state.userType = "UNREGISTERED";
      state.loginSource = null;
      state.userDetails = null;
      state.cards = [];
      sessionStorage.removeItem("businessToken");
      sessionStorage.removeItem("businessRefreshToken");
      sessionStorage.removeItem("businessUserDetails");
      sessionStorage.removeItem("userLoggedToken");
      sessionStorage.removeItem("refreshToken");
    },
    setCards: (state, action) => {
      const all = action.payload || [];
      state.cards = [
        ...all.filter((c) => !c.isCardExpired),
        ...all.filter((c) => c.isCardExpired),
      ];
    },
    removeCard: (state, action) => {
      state.cards = state.cards.filter((c) => c.id !== action.payload);
    },
    setCardsLoading: (state, action) => { state.cardsLoading = action.payload; },
    setDeleteLoading: (state, action) => { state.deleteLoading = action.payload; },
    setSetPrimaryLoading: (state, action) => { state.setPrimaryLoading = action.payload; },
    setAddCardLoading: (state, action) => { state.addCardLoading = action.payload; },
    setSubscribeLoading: (state, action) => { state.subscribeLoading = action.payload; },
    setCancelLoading: (state, action) => { state.cancelLoading = action.payload; },
    setLogoutLoading: (state, action) => { state.logoutLoading = action.payload; },
    setGatewayLoading: (state, action) => { state.gatewayLoading = action.payload; },
    updateGatewayMode: (state, action) => {
      if (state.userDetails) state.userDetails.paymentGatewayMode = action.payload;
    },
  },
});

export const {
  loginStart,
  loginFailed,
  loginSuccess,
  autoLoginSuccess,
  setLoginSource,
  setBusinessUserDetails,
  logoutBusinessLocal,
  setCards,
  removeCard,
  setCardsLoading,
  setDeleteLoading,
  setSetPrimaryLoading,
  setAddCardLoading,
  setSubscribeLoading,
  setCancelLoading,
  setLogoutLoading,
  setGatewayLoading,
  updateGatewayMode,
} = businessAuthSlice.actions;

// Returns a stable device ID for this browser session.
// Stored in sessionStorage so it survives page refreshes within the same tab.
const getWebViewDeviceId = () => {
  let id = sessionStorage.getItem("webViewDeviceId");
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("webViewDeviceId", id);
  }
  return id;
};

// Direct login with userId + password — business users only, no OTP step
export const businessLoginAsync = (data) => async (dispatch) => {
  dispatch(loginStart());
  try {
    const response = await axiosInstanceWebPortal.post("/upload/userLogin", data);
    if (response?.status === 200 || response?.status === 201) {
      const accountType = response?.data?.response?.accountType;
      if (accountType === "admin" || accountType === "masteradmin" || accountType === "customer") {
        dispatch(loginFailed());
        toast.error("This portal is only for business users.");
        return null;
      }
      const token = response.headers["token"];
      const refreshToken = response.headers["refreshtoken"];
      const userDetails = response.data?.response;
  
      sessionStorage.setItem("businessToken", JSON.stringify(token));
      sessionStorage.setItem("businessRefreshToken", JSON.stringify(refreshToken));
      sessionStorage.setItem("businessUserDetails", JSON.stringify(userDetails));

      // Also set as the active axios token so API calls work
      sessionStorage.setItem("userLoggedToken", JSON.stringify(token));
      sessionStorage.setItem("refreshToken", JSON.stringify(refreshToken));

      dispatch(loginSuccess(userDetails));
      // Mark this session as originating from the business portal login page
      dispatch(setLoginSource("businessPortal"));
      toast.success("Login successful!");
      return response;
    }

    dispatch(loginFailed());
    toast.error("Login failed. Please check your credentials.");
    return null;
  } catch (error) {
    dispatch(loginFailed());
    toast.error(
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Login failed."
    );
    return error?.response || null;
  }
};

// Auto-login: token passed from mobile app via URL param
export const logoutBusiness = () => async (dispatch, getState) => {
  dispatch(setLogoutLoading(true));
  const { userDetails } = getState().businessAuth;
  try {
    const rawToken = sessionStorage.getItem("businessToken");
    const token = rawToken ? JSON.parse(rawToken) : null;

    let sessionId = null;
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        sessionId = payload?.sessionId || null;
      } catch { /* malformed token — proceed with local logout */ }
    }

    if (userDetails?.id && sessionId) {
      await axiosInstanceWebPortal.post("/AdminConroller/LogoutUser", {
        id: userDetails.id,
        accType: userDetails.accountType || "business",
        requestType: "business",
        sessionId,
      });
    }
  } catch { /* ignore API errors — always clear local session */ } finally {
    dispatch(setLogoutLoading(false));
    dispatch(logoutBusinessLocal());
  }
};

export const refreshProfileAsync = (userId, accountType) => async (dispatch) => {
  try {
    const response = await axiosInstanceWebPortal.get("/BusisnessUser/GetBuisnessById", {
      params: { userId, accountType },
    });
    if (response?.data) {
      sessionStorage.setItem("businessUserDetails", JSON.stringify(response.data));
      dispatch(autoLoginSuccess(response.data));
      return response.data;
    }
    return false;
  } catch (error) {
    console.error("Profile refresh failed:", error);
    return false;
  }
};

export const GetBuisnessById = (token, userId, accountType) => async (dispatch) => {
  try {
    console.log('userId, accountType',userId, accountType);
console.log('userLoggedToken, businessToken',token);

    sessionStorage.setItem("userLoggedToken", JSON.stringify(token));
    sessionStorage.setItem("businessToken", JSON.stringify(token));

    // Fetch user profile to confirm token is valid
    const response = await axiosInstanceWebPortal.get("/BusisnessUser/GetBuisnessById", {
      params: { userId, accountType },
    });

    if (response?.data) {
      console.log('autologin data',response.data);
      
      sessionStorage.setItem("businessUserDetails", JSON.stringify(response.data));
      dispatch(autoLoginSuccess(response.data));
      return response.data;
    }
    return false;
  } catch (error) {
    console.error("Auto-login failed:", error);
    return false;
  }
};

// ─── Card thunks ─────────────────────────────────────────────────────────────

export const fetchCardsAsync = (userId, accountType) => async (dispatch) => {
  dispatch(setCardsLoading(true));
  try {
    const resp = await axiosInstanceWebPortal.get("/paypal/getCardInfoByBusinessUserId", {
      params: { Id: userId, accountType },
    });
    dispatch(setCards(resp.data?.cardInfo || []));
  } catch {
    toast.error("Failed to load cards.");
  } finally {
    dispatch(setCardsLoading(false));
  }
};

export const deleteCardAsync = (cardId) => async (dispatch, getState) => {
  dispatch(setDeleteLoading(true));
  try {
    const { cards } = getState().businessAuth;
    const deletedCard = cards.find((c) => c.id === cardId);
    const wasPrimary = deletedCard?.isPrimary;

    await axiosInstanceWebPortal.delete("/paypal/DeleteCardById", {
      params: { id: cardId },
    });
    dispatch(removeCard(cardId));
    toast.success("Card deleted.");

    if (wasPrimary) {
      const nextCard = cards.find((c) => c.id !== cardId && !c.isCardExpired);
      if (nextCard) {
        const { userDetails } = getState().businessAuth;
        const userId = userDetails?.id || userDetails?.userId;
        const accountType = userDetails?.accountType;
        await dispatch(setPrimaryCardAsync({
          cardId: nextCard.id,
          businessUserId: userId,
          accountType,
        }));
      }
    }

    return true;
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to delete card.");
    return false;
  } finally {
    dispatch(setDeleteLoading(false));
  }
};

export const setPrimaryCardAsync = (data) => async (dispatch) => {
  dispatch(setSetPrimaryLoading(true));
  try {
    await axiosInstanceWebPortal.post("/paypal/setPrimaryCard", data);
    toast.success("Primary card updated.");
    await dispatch(fetchCardsAsync(data?.businessUserId,data?.accountType));
    return true;
  } catch {
    toast.error("Failed to set primary card.");
    return false;
  } finally {
    dispatch(setSetPrimaryLoading(false));
  }
};

function detectCardType(number) {
  const n = (number || "").replace(/\s/g, "");
  if (/^4/.test(n)) return "Visa";
  if (/^5[1-5]/.test(n)) return "Mastercard";
  if (/^3[47]/.test(n)) return "Amex";
  if (/^6(?:011|5)/.test(n)) return "Discover";
  return "Card";
}

export const addCardAsync = (cardData, userId, accountType) => async (dispatch) => {
  dispatch(setAddCardLoading(true));
  try {
    const pkResp = await axiosInstanceWebPortal.get("/paypal/public-key");
    const publicKey = pkResp.data?.publicKey || pkResp.data?.publicPem || (typeof pkResp.data === "string" ? pkResp.data : null);
    const kid = pkResp.data?.kid || null;

    const cleanNumber = cardData.cardNumber.replace(/\s/g, "");
    const cleanExpiry = cardData.expiry.replace(/\D/g, "");

    const rawCardData = {
      CardNumber: cleanNumber,
      CardHolderName: cardData.cardHolder.trim(),
      ExpiryDate: cleanExpiry,
      CVV: cardData.cvv,
      businessUserId: userId,
      accountType,
    };

    let payload;
    if (publicKey) {
      const { JSEncrypt } = await import("jsencrypt");
      const enc = new JSEncrypt();
      enc.setPublicKey(publicKey);
      const encrypted = enc.encrypt(JSON.stringify(rawCardData));
      if (!encrypted) throw new Error("Encryption failed.");
      payload = {
        encryptedCard: encrypted,
        businessUserId: userId,
        accountType,
        kid,
        ExpiryDate: cleanExpiry,
        cardMeta: {
          last4: cleanNumber.slice(-4),
          holderName: cardData.cardHolder.trim(),
          cardType: detectCardType(cleanNumber),
          ExpiryDate: cleanExpiry,
        },
      };
    } else {
      payload = rawCardData;
    }

    const resp = await axiosInstanceWebPortal.post("/paypal/saveCardInfo", payload);
    if (resp?.data?.success) {
      toast.success("Card added successfully.");
      await dispatch(fetchCardsAsync(userId, accountType));
      await dispatch(refreshProfileAsync(userId, accountType));
      return true;
    }
    toast.error(resp?.data?.message || "Failed to add card.");
    return false;
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to add card.");
    return false;
  } finally {
    dispatch(setAddCardLoading(false));
  }
};

export const subscribeWithCardAsync = (cardId, userId) => async (dispatch, getState) => {
  dispatch(setSubscribeLoading(true));
  try {
    const resp = await axiosInstanceWebPortal.post("/Merchant/createRecurringPayment", {
      cardId,
      businessUserId: userId,
    });
    if (resp?.data?.data?.status === true || resp?.data?.success) {
      toast.success("Subscription activated!");
      const { userDetails } = getState().businessAuth;
      await dispatch(refreshProfileAsync(userId, userDetails?.accountType));
      return true;
    }
    toast.error(resp?.data?.message || "Subscription failed.");
    return false;
  } catch (err) {
    toast.error(err?.response?.data?.message || "Subscription failed.");
    return false;
  } finally {
    dispatch(setSubscribeLoading(false));
  }
};

export const subscribePaypalAsync = (userId) => async (dispatch) => {
  dispatch(setSubscribeLoading(true));
  try {
    const resp = await axiosInstanceWebPortal.post("/paypal/buySubscription", {
      plan_id: ApiConnection.plan_id,
      businessUserId: userId,
    });
    const approvalUrl = resp?.data?.approvalUrl || resp?.data?.url || resp?.data;
    if (typeof approvalUrl === "string" && approvalUrl.startsWith("http")) {
      return approvalUrl;
    }
    toast.error("Could not retrieve PayPal URL. Please try again.");
    return null;
  } catch (err) {
    toast.error(err?.response?.data?.message || "Something went wrong. PayPal is currently unavailable—please choose another payment method to purchase the subscription.");
    return null;
  } finally {
    dispatch(setSubscribeLoading(false));
  }
};

export const cancelSubscriptionAsync = (userId, subscriptionId, gatewayMode) => async (dispatch) => {
  dispatch(setCancelLoading(true));
  try {
    const endpoint =
      gatewayMode === "PAYPAL"
        ? "/paypal/cancelSubscription"
        : "/Merchant/cancel-Subscription";
    const payload =
      gatewayMode === "PAYPAL"
        ? { BusiessUserId: userId, subscription_id: subscriptionId, reason: "User requested cancellation" }
        : { BusiessUserId: userId };
    const resp = await axiosInstanceWebPortal.post(endpoint, payload);
    if (resp?.data?.success) {
      toast.success("Subscription cancelled successfully.");
      return true;
    }
    toast.error("Unable to cancel. Please try again.");
    return false;
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to cancel subscription.");
    return false;
  } finally {
    dispatch(setCancelLoading(false));
  }
};

export const updatePaymentGatewayAsync = (userId, accountType, gateway) => async (dispatch) => {
  dispatch(setGatewayLoading(true));
  try {
    await axiosInstanceWebPortal.post("/BusisnessUser/UpdateBusinessUserTblProfile", {
      userId: userId,
      accountType,
      paymentGatewayMode: gateway,
    });
    dispatch(updateGatewayMode(gateway));
    toast.success(`Payment method set to ${gateway === "MERCHANT" ? "Traditional" : "PayPal"}.`);
    return true;
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to update payment method.");
    return false;
  } finally {
    dispatch(setGatewayLoading(false));
  }
};

export const submitContactSupportAsync = (form) => async () => {
  try {
    await axiosInstanceWebPortal.post("/BusisnessUser/contactSupport", form);
    return true;
  } catch (err) {
    throw new Error(err?.response?.data?.message || "Failed to send message. Please try again.");
  }
};

export default businessAuthSlice.reducer;
